import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Portal, Dialog, Button, Text, useTheme } from 'react-native-paper';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { useUser } from '../hooks/useUser';
import { updatePaymentMethod } from '../api/apiService';

// Stripe publishable key (web)
const STRIPE_PUBLISHABLE_KEY = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || 'pk_test_51RWIRNH2XrByySGIVg3xqGs1t9gHur1HD5FyalpJWz32f2VIdybZfBokm7YLgsjUN4afVFpHvzlH31JtABzwIKNF00x8mZ2PA7';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export interface ChangePaymentMethodModalProps {
  visible: boolean;
  subscription: any;
  onDismiss: () => void;
  onSuccess: () => void;
}

// Payment Form Component
function PaymentForm({ onSuccess, onCancel, clientSecret }: { onSuccess: () => void; onCancel: () => void; clientSecret: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [isElementMounted, setIsElementMounted] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const { userData } = useUser();

  const handleSubmit = async () => {
    if (!stripe || !elements || !isElementMounted) {
      setError('Payment system is not ready. Please try again.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/dashboard',
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'An error occurred during payment confirmation');
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess();
      } else {
        setProcessing(false);
      }
    } catch (e) {
      setError('An unexpected error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <View style={styles.paymentFormContainer}>
      <PaymentElement 
        onReady={() => setIsElementMounted(true)}
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: false,
          },
          defaultValues: {
            billingDetails: {
              name: userData?.full_name || '',
              email: userData?.email || '',
            },
          },
        }}
      />
      {error && (
        <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>
      )}
      <View style={styles.paymentActions}>
        <Button onPress={onCancel} style={styles.actionButton}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          mode="contained"
          onPress={handleSubmit}
          loading={processing}
          disabled={!stripe || !isElementMounted || processing}
          style={styles.actionButton}
          icon="credit-card"
        >
          {processing 
            ? t('changePaymentMethod.processing', 'Processing...') 
            : t('changePaymentMethod.update', 'Update Payment Method')}
        </Button>
      </View>
    </View>
  );
}

export function ChangePaymentMethodModal({ visible, subscription, onDismiss, onSuccess }: ChangePaymentMethodModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    if (visible && subscription?.stripe_subscription_id) {
      handleUpdatePaymentMethod();
    }
  }, [visible, subscription]);

  const handleUpdatePaymentMethod = async () => {
    try {
      const response = await updatePaymentMethod(subscription.stripe_subscription_id);
      
      if (!response.ok) {
        throw new Error(t('changePaymentMethod.error', 'Failed to update payment method'));
      }

      setClientSecret(response.data.client_secret);
    } catch (error: any) {
      setError(error.message);
    }
  };

  if (!clientSecret) {
    return null;
  }

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss} style={{ maxWidth: 600, width: '90%', alignSelf: 'center' }}>
        <Dialog.Title>{t('changePaymentMethod.title', 'Update Payment Method')}</Dialog.Title>
        <Dialog.Content>
          <ScrollView contentContainerStyle={styles.contentContainer}>
            {error && (
              <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>
            )}
            <Elements stripe={stripePromise} options={{ 
              clientSecret,
              appearance: {
                theme: 'stripe',
              },
            }}>
              <PaymentForm
                onSuccess={() => {
                  onSuccess();
                  onDismiss();
                }}
                onCancel={onDismiss}
                clientSecret={clientSecret}
              />
            </Elements>
          </ScrollView>
        </Dialog.Content>
      </Dialog>
    </Portal>
  );
}

const styles = StyleSheet.create({
  paymentFormContainer: {
    padding: 16,
  },
  contentContainer: {
    paddingVertical: 16,
  },
  paymentActions: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  actionButton: {
    minWidth: 120,
  },
  errorText: {
    marginBottom: 16,
    textAlign: 'center',
    fontSize: 16,
    lineHeight: 22,
  },
  description: {
    fontSize: 16,
    lineHeight: 22,
    marginBottom: 16,
  },
}); 